import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Calendar, TrendingUp, Trash2, MessageSquare, Lock, Pencil, Shield, Building2, Dumbbell, ChevronRight, Folder, X, Play, ChevronDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { useCompanyBranding } from "../../hooks/useCompanyBranding";
import VrijeTrainingSection from "./VrijeTrainingSection";
const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

// ── Sporter Bedrijfsschema's Sectie ──────────────────────────────────────────
function SporterSchemasSection({ user }) {
  const [schemas, setSchemas] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedDagIndex, setSelectedDagIndex] = useState(0);
  const { branding, companyNaam } = useCompanyBranding(user);
  const primaryColor = branding?.primary_color || GOLD;
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.token) return;
    fetch(`${API}/api/sporter/schemas`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then(r => r.json())
      .then(d => { setSchemas(d.schemas || []); setFolders(d.folders || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const getFolderNaam = (fid) => folders.find(f => f.id === fid)?.naam || null;
  const getFolderKleur = (fid) => folders.find(f => f.id === fid)?.kleur || primaryColor;

  if (loading) {
    return (
      <div className="account-card mb-5">
        <div className="h-4 bg-white/5 rounded animate-pulse mb-3 w-32" />
        <div className="h-20 bg-white/3 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="account-card mb-5">
      {/* Header: Bedrijfsbadge */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black text-black"
          style={{ background: primaryColor }}>
          {(companyNaam || "C").charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="text-white font-bold text-sm">{companyNaam || "Jouw Sportschool"}</p>
          <p className="text-zinc-600 text-xs">Jouw trainingsschema's</p>
        </div>
      </div>

      {schemas.length === 0 ? (
        <div className="text-center py-6">
          <Dumbbell size={28} className="text-zinc-700 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">Nog geen schema's toegewezen.</p>
          <p className="text-zinc-600 text-xs mt-1">Je coach voegt ze toe zodra ze klaar zijn.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {schemas.map(s => {
            const folderNaam = s.folder_id ? getFolderNaam(s.folder_id) : null;
            const folderKleur = s.folder_id ? getFolderKleur(s.folder_id) : primaryColor;
            return (
              <button
                key={s.id}
                onClick={() => setSelectedSchema(s)}
                data-testid={`sporter-schema-${s.id}`}
                className="w-full text-left rounded-xl border p-3.5 transition-all group hover:border-opacity-50"
                style={{ background: `${primaryColor}06`, borderColor: `${primaryColor}20` }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: `${primaryColor}12`, border: `1px solid ${primaryColor}25` }}>
                      <Dumbbell size={15} style={{ color: primaryColor }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{s.naam}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {s.doel && <span className="text-zinc-500 text-xs">{s.doel}</span>}
                        {folderNaam && (
                          <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                            style={{ background: `${folderKleur}18`, color: folderKleur }}>
                            <Folder size={9} /> {folderNaam}
                          </span>
                        )}
                        {s.niveau && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-500">{s.niveau}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={15} className="text-zinc-700 group-hover:text-zinc-400 transition-colors shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Folders sectie als er mappen zijn */}
      {folders.length > 0 && schemas.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider mb-2">Mappen</p>
          <div className="flex flex-wrap gap-1.5">
            {folders.map(f => (
              <span key={f.id} className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full"
                style={{ background: `${f.kleur}15`, color: f.kleur, border: `1px solid ${f.kleur}25` }}>
                <Folder size={9} /> {f.naam} ({schemas.filter(s => s.folder_id === f.id).length})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Schema detail modal */}
      {selectedSchema && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-[#111] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-white/5 flex-shrink-0">
              <div>
                <p className="text-white font-bold">{selectedSchema.naam}</p>
                {selectedSchema.doel && <p className="text-zinc-500 text-xs mt-0.5">{selectedSchema.doel}</p>}
              </div>
              <button onClick={() => setSelectedSchema(null)} className="text-zinc-500 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-5">
              {/* Meta */}
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedSchema.niveau && (
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/5 text-zinc-400">{selectedSchema.niveau}</span>
                )}
                {selectedSchema.trainingsdagen && (
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/5 text-zinc-400">{selectedSchema.trainingsdagen}× per week</span>
                )}
                {selectedSchema.weken && (
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/5 text-zinc-400">{selectedSchema.weken} weken</span>
                )}
              </div>
              {selectedSchema.beschrijving && (
                <p className="text-zinc-400 text-sm mb-4">{selectedSchema.beschrijving}</p>
              )}
              {/* Dag Picker voor training starten */}
              {selectedSchema.dagen && selectedSchema.dagen.length > 1 && (
                <div className="mb-4">
                  <p className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-2">Kies trainingsdag</p>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedSchema.dagen.map((dag, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedDagIndex(i)}
                        data-testid={`dag-picker-${i}`}
                        className={`p-2.5 rounded-xl border text-left text-xs font-semibold transition-all ${selectedDagIndex === i ? 'text-black' : 'text-zinc-400 border-white/10 bg-white/3'}`}
                        style={selectedDagIndex === i ? { background: primaryColor, borderColor: primaryColor } : {}}>
                        {dag.dag_naam || dag.naam || `Dag ${i + 1}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Trainingsdagen */}
              {selectedSchema.dagen && selectedSchema.dagen.length > 0 ? (
                <div className="space-y-3">
                  {[selectedSchema.dagen[selectedDagIndex]].filter(Boolean).map((dag, i) => (
                    <div key={i} className="bg-[#0f0f0f] border border-white/5 rounded-xl p-3.5">
                      <p className="text-white font-semibold text-sm mb-2">{dag.dag_naam || dag.naam || `Dag ${selectedDagIndex + 1}`}</p>
                      {dag.oefeningen && dag.oefeningen.length > 0 ? (
                        <div className="space-y-1.5">
                          {dag.oefeningen.map((o, j) => (
                            <div key={j} className="flex items-center justify-between text-xs">
                              <span className="text-zinc-300">{o.naam || o.oefening || "Oefening"}</span>
                              <span className="text-zinc-600">
                                {[o.sets && `${o.sets}×`, o.reps, o.gewicht && `${o.gewicht}kg`].filter(Boolean).join(" ")}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-600 text-xs">Geen oefeningen ingevuld</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">Geen trainingsdagen ingevuld</p>
              )}
            </div>
            <div className="p-5 border-t border-white/5 flex-shrink-0 flex gap-2">
              <button onClick={() => setSelectedSchema(null)}
                className="flex-1 py-3 font-bold text-sm rounded-xl text-zinc-400 border border-white/10 bg-white/3 hover:bg-white/5 transition-colors">
                Sluiten
              </button>
              {selectedSchema.dagen && selectedSchema.dagen.length > 0 && (
                <button
                  data-testid="start-training-btn"
                  onClick={() => {
                    // In kiosk/groep modus: sla token op zodat WorkoutCustomLogger
                    // de workout opslaat voor de sporter (niet de trainer).
                    if (user?.token) sessionStorage.setItem("kiosk_token", user.token);
                    setSelectedSchema(null);
                    navigate(`/workout-custom/${selectedSchema.id}/${selectedDagIndex}`);
                  }}
                  className="flex-1 py-3 font-bold text-sm rounded-xl text-black flex items-center justify-center gap-2 transition-all active:scale-95"
                  style={{ background: primaryColor }}>
                  <Play size={14} fill="currentColor" /> Start Training
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mijn Builder Schema's ─────────────────────────────────────────────────────
function MijnBuilderSchemas({ user, navigate }) {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedDagIndex, setSelectedDagIndex] = useState(0);

  useEffect(() => {
    if (!user?.token) return;
    fetch(`${API}/api/schema-builder-access`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then(r => r.json())
      .then(d => {
        setUnlocked(d.unlocked);
        if (d.unlocked) {
          return fetch(`${API}/api/my-builder-schemas`, { headers: { Authorization: `Bearer ${user.token}` } })
            .then(r => r.json())
            .then(data => setSchemas(data.schemas || []));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (loading || !unlocked || schemas.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Mijn schema's</p>
        <button
          onClick={() => navigate("/schema-maker")}
          className="text-xs font-bold px-2.5 py-1 rounded-lg transition-colors"
          style={{ color: GOLD, background: "rgba(201,168,76,0.08)" }}
        >
          Beheren →
        </button>
      </div>

      <div className="space-y-2">
        {schemas.map(s => (
          <button
            key={s.id}
            onClick={() => { setSelectedSchema(s); setSelectedDagIndex(0); }}
            data-testid={`mijn-schema-${s.id}`}
            className="w-full text-left rounded-xl border p-3.5 transition-all group"
            style={{ background: "rgba(201,168,76,0.05)", borderColor: "rgba(201,168,76,0.18)" }}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "rgba(201,168,76,0.10)", border: "1px solid rgba(201,168,76,0.22)" }}>
                  <Dumbbell size={14} style={{ color: GOLD }} />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{s.naam}</p>
                  <p className="text-zinc-600 text-xs">
                    {s.dagen?.length
                      ? `${s.dagen.length} trainingsdag${s.dagen.length !== 1 ? "en" : ""}`
                      : "Leeg schema"}
                    {s.updated_at && (
                      <span> · {new Date(s.updated_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}</span>
                    )}
                  </p>
                </div>
              </div>
              <ChevronRight size={15} className="text-zinc-700 group-hover:text-[#C9A84C] transition-colors shrink-0" />
            </div>
          </button>
        ))}
      </div>

      {/* Detail & start modal */}
      {selectedSchema && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-[#111] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-white/5 flex-shrink-0">
              <div>
                <p className="text-white font-bold">{selectedSchema.naam}</p>
                {selectedSchema.beschrijving && (
                  <p className="text-zinc-500 text-xs mt-0.5">{selectedSchema.beschrijving}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setSelectedSchema(null); navigate(`/schema-maker/${selectedSchema.id}`); }}
                  className="text-zinc-500 hover:text-[#C9A84C] transition-colors"
                  title="Bewerken"
                >
                  <Pencil size={16} />
                </button>
                <button onClick={() => setSelectedSchema(null)} className="text-zinc-500 hover:text-white">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-5">
              {/* Dag selector */}
              {selectedSchema.dagen?.length > 1 && (
                <div className="mb-4">
                  <p className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-2">Kies trainingsdag</p>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedSchema.dagen.map((dag, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedDagIndex(i)}
                        data-testid={`dag-picker-builder-${i}`}
                        className={`p-2.5 rounded-xl border text-left text-xs font-semibold transition-all ${
                          selectedDagIndex === i ? "text-black" : "text-zinc-400 border-white/10 bg-white/3"
                        }`}
                        style={selectedDagIndex === i ? { background: GOLD, borderColor: GOLD } : {}}
                      >
                        {dag.naam || `Dag ${i + 1}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Oefeningen van geselecteerde dag */}
              {selectedSchema.dagen?.length > 0 ? (
                <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-3.5">
                  <p className="text-white font-semibold text-sm mb-2">
                    {selectedSchema.dagen[selectedDagIndex]?.naam || `Dag ${selectedDagIndex + 1}`}
                  </p>
                  {selectedSchema.dagen[selectedDagIndex]?.oefeningen?.length > 0 ? (
                    <div className="space-y-1.5">
                      {selectedSchema.dagen[selectedDagIndex].oefeningen.map((o, j) => (
                        <div key={j} className="flex items-center justify-between text-xs">
                          <span className="text-zinc-300">{o.naam || "Oefening"}</span>
                          <span className="text-zinc-600">
                            {[o.sets && `${o.sets}×`, o.reps, o.gewicht && `${o.gewicht}kg`].filter(Boolean).join(" ")}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-600 text-xs">Geen oefeningen in deze dag</p>
                  )}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">Geen trainingsdagen ingevuld. <button onClick={() => { setSelectedSchema(null); navigate(`/schema-maker/${selectedSchema.id}`); }} className="underline text-[#C9A84C]">Bewerken →</button></p>
              )}
            </div>

            <div className="p-5 border-t border-white/5 flex-shrink-0 flex gap-2">
              <button
                onClick={() => setSelectedSchema(null)}
                className="flex-1 py-3 font-bold text-sm rounded-xl text-zinc-400 border border-white/10 bg-white/3 hover:bg-white/5 transition-colors"
              >
                Sluiten
              </button>
              {selectedSchema.dagen?.length > 0 && selectedSchema.dagen[selectedDagIndex]?.oefeningen?.length > 0 && (
                <button
                  data-testid="start-builder-training-btn"
                  onClick={() => {
                    // In kiosk/groep modus: sla token op voor WorkoutCustomLogger
                    if (user?.token) sessionStorage.setItem("kiosk_token", user.token);
                    setSelectedSchema(null);
                    navigate(`/workout-custom/${selectedSchema.id}/${selectedDagIndex}`);
                  }}
                  className="flex-1 py-3 font-bold text-sm rounded-xl text-black flex items-center justify-center gap-2 transition-all active:scale-95"
                  style={{ background: GOLD }}
                >
                  <Play size={14} fill="currentColor" /> Start Training
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Schema Maker Card ────────────────────────────────────────────────────────
function SchemaMakerCard({ user, navigate }) {
  const [access, setAccess] = useState(null);

  useEffect(() => {
    if (!user?.token) return;
    fetch(`${API}/api/schema-builder-access`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then(r => r.json())
      .then(d => setAccess(d))
      .catch(() => setAccess({ unlocked: false }));
  }, [user]);

  const unlocked = access?.unlocked;

  return (
    <button
      data-testid="schema-maker-card"
      onClick={() => navigate("/schema-maker")}
      className="w-full mb-5 rounded-2xl border text-left transition-all group overflow-hidden"
      style={{
        background: unlocked ? "rgba(201,168,76,0.06)" : "#0f0f0f",
        borderColor: unlocked ? "rgba(201,168,76,0.25)" : "rgba(255,255,255,0.06)",
      }}>
      <div className="flex items-center gap-4 px-4 py-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-all"
          style={{
            background: unlocked ? "rgba(201,168,76,0.12)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${unlocked ? "rgba(201,168,76,0.3)" : "rgba(255,255,255,0.06)"}`,
          }}>
          {unlocked
            ? <Pencil size={18} style={{ color: GOLD }} />
            : <Lock size={18} className="text-zinc-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-bold text-sm ${unlocked ? "text-[#C9A84C]" : "text-zinc-300"}`}>
            Zelf schema maken
          </p>
          <p className="text-zinc-600 text-xs mt-0.5">
            {access === null
              ? "Laden..."
              : unlocked
                ? "Beheer en maak je eigen schema's"
                : "Eenmalig €100 — Lifetime toegang"}
          </p>
        </div>
        {!unlocked && access !== null && (
          <div className="shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold"
            style={{ background: "rgba(201,168,76,0.08)", color: "rgba(201,168,76,0.7)", border: "1px solid rgba(201,168,76,0.15)" }}>
            Vrijzetten
          </div>
        )}
        {unlocked && (
          <div className="shrink-0 text-zinc-600 group-hover:text-[#C9A84C] transition-colors">→</div>
        )}
      </div>
    </button>
  );
}

export default function DashboardSection({ user }) {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [trainerBericht, setTrainerBericht] = useState(null);
  const navigate = useNavigate();

  // Whitelabel branding voor sporters
  const { branding } = useCompanyBranding(user);
  const BRAND = user?.role === "sporter" ? (branding?.primary_color || GOLD) : GOLD;

  const loadStats = () => {
    if (!user?.token) return;
    fetch(`${API}/api/dashboard/stats`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then(r => r.json())
      .then(data => { setStats(data); setLoadingStats(false); })
      .catch(() => setLoadingStats(false));
  };

  useEffect(() => {
    loadStats();
    if (user?.token) {
      fetch(`${API}/api/trainer-bericht`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
        .then(r => r.json())
        .then(d => { if (d.bericht) setTrainerBericht(d); })
        .catch(() => {});
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteLog = async (logId) => {
    setDeletingId(logId);
    try {
      await fetch(`${API}/api/workout-log/${logId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setConfirmId(null);
      setLoadingStats(true);
      loadStats();
    } catch (e) {
      console.error("Delete mislukt:", e);
    }
    setDeletingId(null);
  };

  const initials = user?.name
    ?.split(" ")
    .map(w => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  const activeWeeks = stats?.workouts_per_week?.filter(w => w.count > 0).length || 0;
  const avgPerWeek = stats?.workouts_per_week
    ? (stats.workouts_per_week.reduce((s, w) => s + w.count, 0) / 8).toFixed(1)
    : "0";

  const recentSession = stats?.recent_sessions?.[0];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-[#111] border border-[#C9A84C]/25 rounded-xl px-3 py-2 shadow-lg">
          <p className="text-zinc-400 text-xs mb-0.5">{label}</p>
          <p className="text-[#C9A84C] font-black text-base">
            {payload[0].value} <span className="text-zinc-500 text-xs font-normal">trainingen</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="account-card mb-6">
      {/* Trainer bericht — toont alleen als er een bericht is */}
      {trainerBericht?.bericht && (
        <div className="mb-5 px-4 py-4 rounded-xl border"
          style={{ background: `${GOLD}06`, borderColor: `${GOLD}25` }}>
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={13} style={{ color: GOLD }} />
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Bericht van je trainer</p>
            {trainerBericht.datum && (
              <p className="text-zinc-600 text-[10px] ml-auto">
                {new Date(trainerBericht.datum).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}
              </p>
            )}
          </div>
          <p className="text-zinc-200 text-sm leading-relaxed whitespace-pre-wrap">{trainerBericht.bericht}</p>
        </div>
      )}

      {/* Profile header */}
      <div className="flex items-center gap-4 mb-6">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl shrink-0"
          style={{ background: `${BRAND}18`, border: `2px solid ${BRAND}35` }}
        >
          <span style={{ color: BRAND }}>{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-black text-lg leading-tight truncate">{user?.name}</p>
          {loadingStats ? (
            <p className="text-zinc-600 text-sm">Laden...</p>
          ) : (
            <p className="text-zinc-400 text-sm">
              {stats?.total_sessions || 0} trainingen gelogd
              {recentSession && (
                <span className="text-zinc-600">
                  {" "}· Laatste:{" "}
                  {new Date(recentSession.logged_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Stats rij */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: "Sessies", value: stats?.total_sessions ?? "–", icon: <Activity size={14} /> },
          { label: "Actieve weken", value: activeWeeks, icon: <Calendar size={14} /> },
          { label: "Gem. / week", value: avgPerWeek, icon: <TrendingUp size={14} /> },
        ].map(({ label, value, icon }) => (
          <div key={label} className="rounded-xl p-3 text-center"
            style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.05)" }}>
            <div className="flex justify-center mb-1" style={{ color: BRAND }}>{icon}</div>
            <p className="font-black text-xl text-white leading-none">{loadingStats ? "–" : value}</p>
            <p className="text-zinc-600 text-[10px] mt-0.5 font-medium uppercase tracking-wider">{label}</p>
          </div>
        ))}
      </div>

      {/* Admin-knop — alleen voor admin */}
      {user?.role === "admin" && (
        <button
          data-testid="admin-panel-btn"
          onClick={() => navigate("/admin")}
          className="w-full mb-5 rounded-2xl border text-left transition-all group overflow-hidden"
          style={{ background: "rgba(201,168,76,0.04)", borderColor: "rgba(201,168,76,0.2)" }}>
          <div className="flex items-center gap-4 px-4 py-3.5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.25)" }}>
              <Shield size={17} style={{ color: GOLD }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm" style={{ color: GOLD }}>Admin paneel</p>
              <p className="text-zinc-600 text-xs mt-0.5">Bestellingen, cursisten en schema's beheren</p>
            </div>
            <div className="shrink-0 text-zinc-600 group-hover:text-[#C9A84C] transition-colors">→</div>
          </div>
        </button>
      )}

      {/* Company Admin knop — voor company_admin */}
      {user?.role === "company_admin" && (
        <button
          data-testid="company-admin-panel-btn"
          onClick={() => navigate("/bedrijf-admin")}
          className="w-full mb-5 rounded-2xl border text-left transition-all group overflow-hidden"
          style={{ background: `${BRAND}06`, borderColor: `${BRAND}25` }}>
          <div className="flex items-center gap-4 px-4 py-3.5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `${BRAND}12`, border: `1px solid ${BRAND}30` }}>
              <Building2 size={17} style={{ color: BRAND }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm" style={{ color: BRAND }}>Bedrijf beheren</p>
              <p className="text-zinc-600 text-xs mt-0.5">Sporters, schema's en mappen beheren</p>
            </div>
            <div className="shrink-0 text-zinc-600 transition-colors">→</div>
          </div>
        </button>
      )}

      {/* Schema Maker kaart — niet voor sporters */}
      {user?.role !== "sporter" && <SchemaMakerCard user={user} navigate={navigate} />}

      {/* Eigen builder schema's tonen in dashboard — niet voor sporters */}
      {user?.role !== "sporter" && <MijnBuilderSchemas user={user} navigate={navigate} />}

      {/* Schema's van bedrijf — alleen voor sporters */}
      {user?.role === "sporter" && <SporterSchemasSection user={user} />}

      {/* Vrije Training — voor B2C users (sporters krijgen het via AccountPage) */}
      {user?.role !== "sporter" && <VrijeTrainingSection user={user} />}

      {/* Bar chart */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-white font-bold text-sm">Trainingen per week</p>
          <p className="text-zinc-600 text-xs">Afgelopen 8 weken</p>
        </div>
        {loadingStats ? (
          <div className="h-36 bg-white/3 rounded-xl animate-pulse" />
        ) : (
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={stats?.workouts_per_week || []} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="week" tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(201,168,76,0.06)" }} />
              <Bar dataKey="count" fill={GOLD} radius={[5, 5, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Recent sessions met prullenbak */}
      {!loadingStats && stats?.recent_sessions?.length > 0 && (
        <div className="mt-5 pt-5 border-t border-white/5">
          <p className="text-zinc-600 text-xs uppercase tracking-wider font-bold mb-3">Recente trainingen</p>
          <div className="space-y-2">
            {stats.recent_sessions.slice(0, 5).map((s, i) => (
              <div key={s.log_id || i} className="group flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: GOLD }} />
                  <p className="text-zinc-300 text-sm truncate font-medium">
                    {s.training_naam || s.session_naam || s.schema_key}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <p className="text-zinc-600 text-xs">
                    {new Date(s.logged_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}
                  </p>
                  {confirmId === (s.log_id || i) ? (
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`confirm-delete-log-${i}`}
                        onClick={() => handleDeleteLog(s.log_id)}
                        disabled={deletingId === s.log_id}
                        className="text-[10px] font-bold px-2 py-0.5 rounded-lg transition-all"
                        style={{ background: "rgba(239,68,68,0.15)", color: "#f87171" }}
                      >
                        {deletingId === s.log_id ? "..." : "Ja"}
                      </button>
                      <button
                        onClick={() => setConfirmId(null)}
                        className="text-[10px] font-bold px-2 py-0.5 rounded-lg transition-all"
                        style={{ background: "rgba(255,255,255,0.06)", color: "#71717a" }}
                      >
                        Nee
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid={`delete-log-btn-${i}`}
                      onClick={() => setConfirmId(s.log_id || i)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-red-500/10"
                      title="Training verwijderen"
                    >
                      <Trash2 size={13} className="text-zinc-600 hover:text-red-400 transition-colors" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lege staat */}
      {!loadingStats && (stats?.total_sessions ?? 0) === 0 && (
        <div className="mt-4 pt-4 border-t border-white/5 text-center">
          <p className="text-zinc-600 text-xs mb-2">Nog geen trainingen gelogd.</p>
          <button
            onClick={() => navigate("/account")}
            className="text-xs font-bold px-4 py-2 rounded-xl"
            style={{ background: `${GOLD}15`, color: GOLD }}
          >
            Open een schema om te beginnen
          </button>
        </div>
      )}
    </div>
  );
}
